import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ScissorsIcon,
} from '@heroicons/react/20/solid';
import { DocumentIcon, FolderIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type ExtractionContext,
  PdfPageExtractorModal,
} from '@/components/pdf-extraction';
import { Button } from '@/components/ui/catalyst/button';
import { Checkbox } from '@/components/ui/catalyst/checkbox';
import { Dialog, DialogTitle } from '@/components/ui/catalyst/dialog';
import { Text } from '@/components/ui/catalyst/text';
import { Z_INDEX_MODAL_DEEPLY_NESTED } from '@/components/ui/zIndex';
import { cn } from '@/lib/utils';
import { useGetBlobsQuery } from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import type { Blob as BlobType } from '@/services/types/blob-types';
import type { CollectionNode } from '@/services/types/collection-types';

interface DocumentSelectorProps {
  matterId: string;
  selectedBlobIds: string[];
  onSelectionChange: (blobIds: string[]) => void;
  onConfirm: () => void;
  filterBlobs?: (blob: BlobType) => boolean;
  multiSelect?: boolean;
  className?: string;
  showConfirmButton?: boolean;
  confirmButtonLabel?: string;
  enablePreview?: boolean;
  previewDialogTitle?: string;
  /** Enable page extraction for PDFs */
  enableExtraction?: boolean;
  /** Context for extraction (affects success actions) */
  extractionContext?: ExtractionContext;
  /** Callback when extraction is complete */
  onExtractComplete?: (newBlobId: string) => void;
}

// Utility functions
const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / 1024 ** i) * 100) / 100} ${sizes[i]}`;
};

const getBlobDisplayName = (blob: BlobType): string => {
  return blob.name || blob.original_filename || 'Unnamed document';
};

const getFileExtension = (blob: BlobType): string => {
  const filename = blob.original_filename || blob.name || '';
  const ext = filename.split('.').pop()?.toUpperCase() || 'FILE';
  return ext;
};

const documentFilter = (blob: BlobType): boolean => {
  const filename = blob.original_filename || blob.name || '';
  return (
    filename.toLowerCase().endsWith('.pdf') ||
    filename.toLowerCase().endsWith('.docx') ||
    filename.toLowerCase().endsWith('.doc')
  );
};

/**
 * Check if a blob is a PDF file
 */
const isPdfBlob = (blob: BlobType): boolean => {
  const filename = (blob.original_filename || blob.name || '').toLowerCase();
  return filename.endsWith('.pdf');
};

/**
 * Document list item component
 */
interface DocumentListItemProps {
  blob: BlobType;
  isSelected: boolean;
  onToggle: (blobId: string) => void;
  multiSelect: boolean;
}

const DocumentListItem = React.memo<DocumentListItemProps>(
  ({
    blob,
    isSelected,
    onToggle,
    multiSelect,
  }: DocumentListItemProps) => {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle(blob.id);
      }
    };

    return (
      <div
        className={cn(
          'flex items-center px-3 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100 last:border-0',
          isSelected && 'bg-indigo-50 hover:bg-indigo-50',
        )}
        onClick={() => onToggle(blob.id)}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={`Select ${getBlobDisplayName(blob)}`}
        aria-pressed={isSelected}
      >
        {multiSelect && (
          <Checkbox
            className="mr-3"
            checked={isSelected}
            onChange={() => onToggle(blob.id)}
          />
        )}
        <DocumentIcon
          className={cn(
            'h-5 w-5 mr-3 flex-shrink-0',
            isSelected ? 'text-indigo-500' : 'text-gray-400',
          )}
        />
        <div className="flex-1 min-w-0">
          <div
            className={cn(
              'font-medium text-sm truncate',
              isSelected ? 'text-indigo-900' : 'text-gray-900',
            )}
          >
            {getBlobDisplayName(blob)}
          </div>
          <div
            className={cn(
              'text-xs mt-0.5',
              isSelected ? 'text-indigo-600' : 'text-gray-500',
            )}
          >
            {formatFileSize(blob.file_size)} • {getFileExtension(blob)}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-3">
          {!multiSelect && isSelected && (
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-indigo-600" />
            </div>
          )}
        </div>
      </div>
    );
  },
);

DocumentListItem.displayName = 'DocumentListItem';

/**
 * Component for selecting documents from matter files
 */
export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  matterId,
  selectedBlobIds,
  onSelectionChange,
  onConfirm,
  filterBlobs = documentFilter,
  multiSelect = false,
  className = '',
  showConfirmButton = true,
  confirmButtonLabel,
  enablePreview = false,
  previewDialogTitle = 'Document Preview',
  enableExtraction = false,
  extractionContext = 'general',
  onExtractComplete,
}) => {
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId, {
    skip: !matterId,
  });
  const { data: blobs, isLoading, refetch } = useGetBlobsQuery(matterId, {
    skip: !matterId,
  });

  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewBlob, setPreviewBlob] = useState<BlobType | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Extraction state
  const [extractionBlob, setExtractionBlob] = useState<BlobType | null>(null);

  // Handle extraction complete
  const handleExtractionComplete = useCallback(
    (newBlobId: string) => {
      setExtractionBlob(null);
      refetch();
      // Auto-select the extracted blob
      onSelectionChange([newBlobId]);
      onExtractComplete?.(newBlobId);
    },
    [refetch, onSelectionChange, onExtractComplete],
  );

  // Set root collection as default when data is loaded
  useEffect(() => {
    if (collectionTree && !selectedCollectionId) {
      setSelectedCollectionId(collectionTree.id);
    }
  }, [collectionTree, selectedCollectionId]);

  // Memoized filtered blobs
  const filteredBlobs = useMemo(() => {
    if (!blobs) return [];

    return blobs.filter(
      (blob) =>
        filterBlobs(blob) &&
        (selectedCollectionId === collectionTree?.id ||
          blob.collection_details.id === selectedCollectionId) &&
        (searchQuery === '' ||
          blob.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          blob.original_filename
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())),
    );
  }, [blobs, selectedCollectionId, collectionTree, searchQuery, filterBlobs]);

  // Get selected blob objects
  const selectedBlobs = useMemo(() => {
    if (!blobs) return [];
    return blobs.filter((blob) => selectedBlobIds.includes(blob.id));
  }, [blobs, selectedBlobIds]);

  // Memoized callbacks
  const handleToggleSelect = useCallback(
    (blobId: string) => {
      if (multiSelect) {
        if (selectedBlobIds.includes(blobId)) {
          onSelectionChange(selectedBlobIds.filter((id) => id !== blobId));
        } else {
          onSelectionChange([...selectedBlobIds, blobId]);
        }
      } else {
        onSelectionChange(selectedBlobIds.includes(blobId) ? [] : [blobId]);
      }
    },
    [selectedBlobIds, onSelectionChange, multiSelect],
  );

  // Build collection options for select
  const buildCollectionOptions = useCallback(
    (
      node: CollectionNode,
      level = 0,
    ): Array<{ value: string; label: string }> => {
      const options: Array<{ value: string; label: string }> = [];
      const prefix = '\u00A0\u00A0'.repeat(level);

      options.push({
        value: node.id,
        label: prefix + (node.name === 'root' ? 'All Files' : node.name),
      });

      if (node.children) {
        node.children.forEach((child: CollectionNode) => {
          options.push(...buildCollectionOptions(child, level + 1));
        });
      }

      return options;
    },
    [],
  );

  const collectionOptions = useMemo(() => {
    if (!collectionTree) return [];
    return buildCollectionOptions(collectionTree);
  }, [collectionTree, buildCollectionOptions]);

  useEffect(() => {
    if (!enablePreview) {
      return undefined;
    }
    let objectUrl: string | null = null;
    let isMounted = true;

    const loadPreview = async () => {
      if (!previewBlob?.download_url) {
        setPreviewUrl(null);
        setPreviewError(null);
        setIsPreviewLoading(false);
        return;
      }

      setIsPreviewLoading(true);
      setPreviewError(null);

      try {
        const response = await fetch(previewBlob.download_url);
        if (!response.ok) throw new Error('Failed to load preview');
        const fileBlob = await response.blob();
        objectUrl = URL.createObjectURL(fileBlob);
        if (isMounted) {
          setPreviewUrl(objectUrl);
        }
      } catch (error) {
        if (isMounted) {
          setPreviewError('Unable to load document preview');
          setPreviewUrl(null);
        }
      } finally {
        if (isMounted) {
          setIsPreviewLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [previewBlob, enablePreview]);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Search and Filter Row */}
      <div className="grid grid-cols-2 gap-2">
        {/* Search Input */}
        <div className="col-span-2">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              type="search"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-12 w-full rounded-[20px] border border-zinc-200 bg-white pl-12 pr-4 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-indigo-400 focus:outline-none"
            />
          </div>
        </div>

        {/* Folder Select */}
        <div className="relative col-span-2">
          <select
            value={selectedCollectionId}
            onChange={(e) => setSelectedCollectionId(e.target.value)}
            className="h-12 w-full appearance-none rounded-[20px] border border-zinc-200 bg-white px-4 pr-10 text-base text-zinc-900 focus:border-indigo-400 focus:outline-none"
          >
            {collectionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <svg
            className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Document List */}
      <div
        className="border rounded-lg max-h-96 overflow-y-auto bg-white"
        role="region"
        aria-label="Document list"
      >
        {isLoading && (
          <div className="flex items-center justify-center h-40">
            <div className="text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 mx-auto" />
              <Text className="mt-3 text-sm text-gray-600">
                Loading documents...
              </Text>
            </div>
          </div>
        )}

        {!isLoading && filteredBlobs.length > 0 && (
          <div>
            {filteredBlobs.map((blob) => (
              <DocumentListItem
                key={blob.id}
                blob={blob}
                isSelected={selectedBlobIds.includes(blob.id)}
                onToggle={handleToggleSelect}
                multiSelect={multiSelect}
              />
            ))}
          </div>
        )}

        {!isLoading && filteredBlobs.length === 0 && (
          <div className="flex flex-col justify-center items-center h-40 p-8 text-gray-500">
            <FolderIcon className="h-12 w-12 text-gray-300 mb-3" />
            <Text className="text-sm font-medium">No documents found</Text>
            {searchQuery ? (
              <Text className="text-xs mt-1 text-gray-400">
                Try adjusting your search
              </Text>
            ) : (
              <Text className="text-xs mt-1 text-gray-400">
                No PDF or DOCX files in this folder
              </Text>
            )}
          </div>
        )}
      </div>

      {/* Selected Documents Summary */}
      {selectedBlobs.length > 0 && multiSelect && (
        <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleIcon className="h-4 w-4 text-indigo-600" />
            <Text className="text-sm font-semibold text-indigo-900">
              {selectedBlobs.length} Selected
            </Text>
          </div>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {selectedBlobs.map((blob) => (
              <div key={blob.id} className="flex items-center gap-2">
                <div className="h-1 w-1 bg-indigo-400 rounded-full flex-shrink-0" />
                <Text className="text-xs text-indigo-800 truncate">
                  {getBlobDisplayName(blob)}
                </Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      {showConfirmButton && selectedBlobIds.length > 0 && (
        <div className="sticky bottom-0 pt-4 border-t bg-white">
          <Button
            onClick={onConfirm}
            color="indigo"
            className="w-full shadow-sm"
          >
            {confirmButtonLabel ??
              (multiSelect && selectedBlobIds.length > 1
                ? `Add ${selectedBlobIds.length} Documents`
                : 'Add Document')}
          </Button>
        </div>
      )}

      {enablePreview && (
        <Dialog
          open={!!previewBlob}
          onClose={() => setPreviewBlob(null)}
          zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
          nested
        >
          <DialogTitle>{previewDialogTitle}</DialogTitle>
          <div className="mt-4 h-[70vh]">
            {isPreviewLoading && (
              <div className="h-full flex items-center justify-center text-sm text-gray-500">
                Loading preview...
              </div>
            )}
            {!isPreviewLoading && previewError && (
              <div className="h-full flex items-center justify-center text-sm text-red-500">
                {previewError}
              </div>
            )}
            {!isPreviewLoading && previewUrl && (
              <iframe
                title="Document preview"
                src={`${previewUrl}#toolbar=0`}
                className="w-full h-full rounded border"
              />
            )}
          </div>
          {previewBlob && (
            <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
              <span className="truncate pr-4">
                {getBlobDisplayName(previewBlob)}
              </span>
              <div className="flex items-center gap-4">
                {enableExtraction && isPdfBlob(previewBlob) && (
                  <button
                    type="button"
                    onClick={() => {
                      setExtractionBlob(previewBlob);
                      setPreviewBlob(null);
                    }}
                    className="flex items-center gap-1 text-amber-600 hover:text-amber-800 font-medium"
                  >
                    <ScissorsIcon className="h-4 w-4" />
                    Extract Pages
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    window.open(previewBlob.download_url, '_blank', 'noopener')
                  }
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Open in new tab
                </button>
              </div>
            </div>
          )}
        </Dialog>
      )}

      {/* PDF Page Extraction Modal */}
      {enableExtraction && extractionBlob && (
        <PdfPageExtractorModal
          isOpen={!!extractionBlob}
          onClose={() => setExtractionBlob(null)}
          matterId={matterId}
          sourceBlobId={extractionBlob.id}
          sourceUrl={extractionBlob.download_url}
          sourceName={getBlobDisplayName(extractionBlob)}
          defaultCollectionId={extractionBlob.collection_id}
          context={extractionContext}
          onExtractComplete={handleExtractionComplete}
          zIndex={Z_INDEX_MODAL_DEEPLY_NESTED}
          nested
        />
      )}
    </div>
  );
};
